import type { ComponentTemplate } from "@v1/types/component-template";
import type {
  ComponentTemplateCategoryId,
  ComponentTemplateIndustryId,
} from "schemas/componentTemplates";
import type { ComponentTemplateScope } from "schemas/generated/componentTemplates";
import type { ReploElementType } from "schemas/generated/element";

import React, { useState } from "react";

import DeprecatedButton from "@v1/components/designSystem/button/Button";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { routes } from "@v2/utils/router";
import { ArrowLeft } from "lucide-react";
import { generatePath, useNavigate, useSearchParams } from "react-router-dom";
import { CATEGORIES_IDS } from "schemas/componentTemplates";
import { isReploElementType } from "schemas/element";

import SearchTemplates from "./SearchTemplates";
import TemplateFilters from "./TemplateFilters";
import TemplatePage from "./TemplatePage";

export type TemplateDevices = "mobile" | "desktop";

const ChooseTemplateOverlay = () => {
  const navigate = useNavigate();
  const projectId = useCurrentProjectId();
  const [searchParams] = useSearchParams();

  const [selectedIndustryIds, setSelectedIndustryIds] = useState<
    ComponentTemplateIndustryId[]
  >([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<
    ComponentTemplateCategoryId[]
  >([]);
  const [previewPlatform, setPreviewPlatform] =
    React.useState<TemplateDevices>("desktop");
  const [subpageTemplate, setSubpageTemplate] =
    useState<ComponentTemplate | null>(null);
  const [templateScope, setTemplateScope] =
    useState<Omit<ComponentTemplateScope, "left-bar" | "unlisted">>("global");

  const handleBack = () => {
    navigate(generatePath(routes.shops.site, { projectId }));
  };

  const defaultElementType = (() => {
    const elementType = searchParams.get("elementType");
    const isValidElementType = elementType
      ? isReploElementType(elementType)
      : false;

    if (isValidElementType) {
      return elementType as ReploElementType;
    }

    if (
      selectedCategoryIds.length === 1 &&
      selectedCategoryIds[0] === CATEGORIES_IDS.productPageTemplates
    ) {
      return "shopifyProductTemplate";
    }

    return "page";
  })();

  return (
    <div className="bg-neutral-soft flex h-full w-full items-center justify-center">
      {subpageTemplate ? (
        <TemplatePage
          template={subpageTemplate}
          setSubpageTemplate={setSubpageTemplate}
          previewPlatform={previewPlatform}
          setPreviewPlatform={setPreviewPlatform}
          defaultElementType={defaultElementType}
        />
      ) : (
        <div className="border-0.5 border-border ml-19 mr-2 mt-2 flex h-[calc(100vh-16px)] w-full flex-col items-center gap-12 overflow-hidden rounded-xl bg-white pl-24 pr-24 pt-12">
          <div className="relative flex w-full items-center justify-center">
            <DeprecatedButton
              variant="secondary"
              size="base"
              layoutClassName="absolute left-0"
              onClick={handleBack}
              startEnhancer={<ArrowLeft size={16} />}
            >
              Back
            </DeprecatedButton>
            <div className="text-2xl font-semibold">
              What would you like to build today?
            </div>
          </div>

          <div className="flex w-full gap-6">
            <TemplateFilters
              selectedIndustryIds={selectedIndustryIds}
              setSelectedIndustryIds={setSelectedIndustryIds}
              selectedCategoryIds={selectedCategoryIds}
              setSelectedCategoryIds={setSelectedCategoryIds}
              templateScope={templateScope}
              setTemplateScope={setTemplateScope}
            />
            <SearchTemplates
              selectedIndustryIds={selectedIndustryIds}
              selectedCategoryIds={selectedCategoryIds}
              setSubpageTemplate={setSubpageTemplate}
              previewPlatform={previewPlatform}
              setPreviewPlatform={setPreviewPlatform}
              templateScope={templateScope}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChooseTemplateOverlay;
